import { useState } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card } from '../ui/Card'
import { X, Percent, DollarSign } from 'lucide-react'

interface DiscountModalProps {
  isOpen: boolean
  onClose: () => void
  onApply: (type: 'fixed' | 'percentage', value: number) => void
  title: string
  currentPrice?: number
}

export const DiscountModal = ({ 
  isOpen, 
  onClose, 
  onApply, 
  title,
  currentPrice 
}: DiscountModalProps) => {
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('percentage')
  const [discountValue, setDiscountValue] = useState('')

  const handleApply = () => {
    const value = parseFloat(discountValue)
    if (value > 0) {
      onApply(discountType, value)
      setDiscountValue('')
      onClose()
    }
  }

  const handleQuickDiscount = (type: 'fixed' | 'percentage', value: number) => {
    onApply(type, value)
    onClose()
  }

  if (!isOpen) return null

  const maxPercentage = 100
  const maxFixed = currentPrice || 1000

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Quick Discount Buttons */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Quick Discounts
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('percentage', 5)}
                className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
              >
                <Percent className="h-3 w-3 mr-1" />
                5% Off
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('percentage', 10)}
                className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
              >
                <Percent className="h-3 w-3 mr-1" />
                10% Off
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('percentage', 15)}
                className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
              >
                <Percent className="h-3 w-3 mr-1" />
                15% Off
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('percentage', 20)}
                className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
              >
                <Percent className="h-3 w-3 mr-1" />
                20% Off
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('fixed', 10)}
                className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <DollarSign className="h-3 w-3 mr-1" />
                ₹10 Off
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('fixed', 25)}
                className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <DollarSign className="h-3 w-3 mr-1" />
                ₹25 Off
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('fixed', 50)}
                className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <DollarSign className="h-3 w-3 mr-1" />
                ₹50 Off
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickDiscount('fixed', 100)}
                className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <DollarSign className="h-3 w-3 mr-1" />
                ₹100 Off
              </Button>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Custom Discount
            </label>
          </div>

          {/* Discount Type Selection */}
          <div className="flex gap-2">
            <Button
              variant={discountType === 'percentage' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDiscountType('percentage')}
              className="flex-1"
            >
              <Percent className="h-3 w-3 mr-1" />
              Percentage
            </Button>
            <Button
              variant={discountType === 'fixed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDiscountType('fixed')}
              className="flex-1"
            >
              <DollarSign className="h-3 w-3 mr-1" />
              Fixed Amount
            </Button>
          </div>

          {/* Discount Value Input */}
          <div className="space-y-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">
              {discountType === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
            </label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                max={discountType === 'percentage' ? maxPercentage : maxFixed}
                step={discountType === 'percentage' ? '1' : '0.01'}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                className="text-right pr-8"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                {discountType === 'percentage' ? '%' : '₹'}
              </span>
            </div>
            
            {/* Validation */}
            {discountValue && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {discountType === 'percentage' && parseFloat(discountValue) > 100 && (
                  <span className="text-red-500">Percentage cannot exceed 100%</span>
                )}
                {discountType === 'fixed' && currentPrice && parseFloat(discountValue) >= currentPrice && (
                  <span className="text-red-500">Discount cannot exceed item price</span>
                )}
              </div>
            )}
          </div>

          {/* Preview */}
          {discountValue && currentPrice && (
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm">
              <div className="flex justify-between">
                <span>Original Price:</span>
                <span>₹{currentPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-green-600 dark:text-green-400">
                <span>Discount:</span>
                <span>
                  -{discountType === 'percentage' 
                    ? `₹${((currentPrice * parseFloat(discountValue)) / 100).toFixed(2)}`
                    : `₹${parseFloat(discountValue).toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between font-semibold border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                <span>Final Price:</span>
                <span>
                  ₹{(discountType === 'percentage' 
                    ? currentPrice - (currentPrice * parseFloat(discountValue)) / 100
                    : currentPrice - parseFloat(discountValue)).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleApply} 
            disabled={!discountValue || parseFloat(discountValue) <= 0}
            className="flex-1"
          >
            Apply Discount
          </Button>
        </div>
      </Card>
    </div>
  )
}